import './index.css';
import 'assets/scss/style.scss';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/carousel/styles.css';

import './styles/responsive.css';

import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Notifications } from '@mantine/notifications';
import ThemeProvider from './theme/ThemeProvider';
import { useThemeMode } from './theme/useThemeMode';
import { semanticPalette } from './theme/semanticColors';

import { SnackbarProvider } from 'notistack';

import Routes from './routes';

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import useMetaPixel from 'hooks/use-meta-pixel';

import clearReduxStore from 'utils/clear-redux-store';
import { store } from './store';
import { setLoggedOut } from 'store/features/userSlice';

const stripePromise = loadStripe(import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY);

const stripeElementOptionsByMode = {
  dark: {
    appearance: {
      theme: 'night',
      variables: {
        fontFamily: 'Sohne, system-ui, sans-serif',
        fontWeightNormal: '500',
        borderRadius: '8px',
        colorBackground: '#0A2540',
        colorPrimary: '#EFC078',
        accessibleColorOnColorPrimary: '#1A1B25',
        colorText: 'white',
        colorTextSecondary: 'white',
        colorTextPlaceholder: '#ABB2BF',
        tabIconColor: 'white',
        logoColor: 'dark'
      },
      rules: {
        '.Input': {
          backgroundColor: '#212D63',
          border: '1px solid var(--colorPrimary)'
        }
      },
      layout: {
        type: 'tabs',
        defaultCollapsed: false
      }
    }
  },
  light: {
    appearance: {
      theme: 'stripe',
      variables: {
        fontFamily: 'Sohne, system-ui, sans-serif',
        fontWeightNormal: '500',
        borderRadius: '8px',
        colorBackground: semanticPalette.light.surface.base,
        colorPrimary: '#f05776',
        colorText: semanticPalette.light.text.primary,
        colorTextSecondary: semanticPalette.light.text.secondary,
        colorTextPlaceholder: semanticPalette.light.text.muted
      },
      rules: {
        '.Input': {
          backgroundColor: semanticPalette.light.surface.raised,
          border: `1px solid ${semanticPalette.light.border.default}`
        }
      },
      layout: {
        type: 'tabs',
        defaultCollapsed: false
      }
    }
  }
};

// Stripe Elements appearance follows the color scheme; must render inside
// ThemeProvider to read the mode.
const StripeElementsProvider = ({ children }) => {
  const { mode } = useThemeMode();
  const options = useMemo(() => stripeElementOptionsByMode[mode], [mode]);

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
};

StripeElementsProvider.propTypes = {
  children: PropTypes.node
};

// eslint-disable-next-line arrow-body-style
const App = () => {
  useMetaPixel('1042248837247384');

  // Cross-tab logout: when another tab sets isLoggedIn to 'false', force logout in this tab
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'isLoggedIn' && event.newValue === 'false') {
        store.dispatch(setLoggedOut());
        clearReduxStore();
        sessionStorage.clear();
        window.location.assign('/login');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <ThemeProvider>
      <GoogleOAuthProvider clientId={import.meta.env.VITE_APP_AUTH_GOOGLE_CLIENT_ID}>
        <Notifications position="top-right" zIndex={1700} />
        <SnackbarProvider maxSnack={3}>
          <StripeElementsProvider>
            <Routes />
          </StripeElementsProvider>
        </SnackbarProvider>
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
};

export default App;
